import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { Button, Flex, FlexItem, Link, Text } from '@bigcommerce/big-design';
import { ArrowBackIcon } from '@bigcommerce/big-design-icons';
import Attributes from './Attributes';
import { useClient } from '../../../../context/client';
import {
	ORDER_ACTIONS,
	TAG_TYPES,
	buildStateWithAddedTag,
} from '../hooks/useOrderEditState';
import { useSaveOrder } from '../hooks/useSaveOrder';

const OrderActions = styled(Flex).attrs({ className: 'order-heading' })`
	gap: 0;
`;

const InReviewButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #f2994a;
	border-radius: 4px;
	background: #f2994a;
	color: #fff;
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	height: 36px;
	padding: 0 12px;
	line-height: 1;
	transition: background 120ms ease, border-color 120ms ease;
	margin-top: 10px;

	@media only screen and (min-width: 720px) {
		position: relative;
		left: 4rem;
		margin-top: 0;
	}

	&:hover,
	&:focus {
		background: #ea8a3a;
		border-color: #ea8a3a;
		color: #fff;
	}

	&:disabled,
	&:disabled:hover,
	&:disabled:focus {
		cursor: not-allowed;
		background: #d9d9d9;
		border-color: #d9d9d9;
		color: #6b6b6b;
		opacity: 1;
	}
`;

function usePdfDownload(orderId) {
	const client = useClient();
	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useCallback(async () => {
		setIsLoading(true);
		try {
			// TODO: Show message alert if error happens
			await client.downloadOrders([orderId]);
		} finally {
			setIsLoading(false);
		}
	}, [orderId]);

	return [isLoading, dispatch];
}

export default function Header({
	orderState,
	onPhotosNavigate,
	orderDispatch,
}) {
	const params = useParams();
	const navigate = useNavigate();
	const [isDownloadingPdf, downloadPdf] = usePdfDownload(
		orderState?.order?.id,
	);
	const [isSaving, saveOrder] = useSaveOrder();
	const confirmation = orderState?.order?.date_created
		? new Date(orderState.order.date_created).getTime().toString()
		: '';
	const encodedParameters = new URLSearchParams();
	encodedParameters.set('orderId', orderState?.order?.id ?? '');
	encodedParameters.set('confirmation', confirmation);
	const imageUploadUrl = `https://www.shearcomfort.com/order-images?${encodedParameters.toString()}`;
	const handleMoveToInReview = useCallback(async () => {
		const tag = 'In Review';
		const hasInReviewTag = orderState?.tags?.includes(tag);
		if (hasInReviewTag) {
			return;
		}

		const updatedOrderState = buildStateWithAddedTag(
			orderState,
			TAG_TYPES.TAGS,
			tag,
		);

		const didSave = await saveOrder(updatedOrderState, orderDispatch);

		if (!didSave) {
			return;
		}

		orderDispatch({
			type: ORDER_ACTIONS.ADD_TAG,
			data: { type: TAG_TYPES.TAGS, tag },
		});

		if (orderState?.nextOrderId) {
			navigate(`/orders/${orderState.nextOrderId}`);
		}
	}, [navigate, orderDispatch, orderState, saveOrder]);

	return (
		<Flex
			flexDirection="column"
			borderBottom="box"
			paddingBottom="medium"
			marginBottom="xxxLarge"
			className="tags-container"
		>
			<div className="order-heading-container">
				<FlexItem padding="xSmall">
					<Link
						className="all-orders-link all-orders-link--mobile"
						onClick={() => navigate('/orders')}
					>
						<ArrowBackIcon
							style={{
								marginRight: '2px',
								marginTop: '-2px',
							}}
						/>{' '}
						All Orders
					</Link>
					<Flex className="order-heading">
						<Text marginBottom="none">Order '{params.id}'</Text>
						<Button
							className="order-heading-button"
							disabled={!orderState.previousOrderId}
							onClick={() =>
								navigate(
									`/orders/${orderState.previousOrderId}`,
								)
							}
						>
							Previous Order
						</Button>
						<Button
							className="order-heading-button"
							disabled={!orderState.nextOrderId}
							onClick={() =>
								navigate(`/orders/${orderState.nextOrderId}`)
							}
						>
							Next Order
						</Button>
						<InReviewButton
							className="order-heading-button"
							disabled={
								isSaving || orderState?.tags?.includes('In Review')
							}
							onClick={handleMoveToInReview}
						>
							{isSaving ? 'Saving...' : 'In Review'}
						</InReviewButton>
					</Flex>
				</FlexItem>
				<FlexItem padding="xSmall">
					<OrderActions>
						<Link
							className="all-orders-link all-orders-link--desktop"
							onClick={() => navigate('/orders')}
						>
							<ArrowBackIcon
								style={{
									marginRight: '2px',
									marginTop: '-2px',
								}}
							/>{' '}
							All Orders
						</Link>
						<Button
							variant="secondary"
							isLoading={isDownloadingPdf}
							disabled={isDownloadingPdf}
							onClick={downloadPdf}
						>
							Download PDF
						</Button>
						<Button variant="secondary" onClick={onPhotosNavigate}>
							Photos
						</Button>
						<Button
							disabled={isSaving}
							isLoading={isSaving}
							variant="primary"
							onClick={() => saveOrder(orderState, orderDispatch)}
						>
							Save
						</Button>
					</OrderActions>
				</FlexItem>
			</div>

			<Attributes
				orderDispatch={orderDispatch}
				orderStatus={orderState.order.status_id}
				selectedTags={orderState.tags}
				selectedChildTags={orderState.childTags}
				imageUploadUrl={imageUploadUrl}
			/>
		</Flex>
	);
}
